import React from 'react'
import { makeStyles } from '@material-ui/core'
import { DateObject } from 'react-multi-date-picker'
import CalendarComponent from './Calendar'
import UserAvatar from '../shared/UserAvatar'
import { extractMonth } from '../../utils/extractDate'
import moment from 'moment'

const useStyles = makeStyles(theme => ({
  root: {
    paddingBottom: 16,
    gap: 16,
    display: 'flex',
    width: '100%',
    overflowX: 'auto',
    alignItems: 'flex-start',
    fontFamily: 'Montserrat, Source Sans Pro',
    '& .rmdp-arrow-container:hover': {
      backgroundColor: 'unset',
      '& .rmdp-arrow': {
        border: 'solid #555555',
        borderWidth: '0 2px 2px 0'
      }
    },
    '& .rmdp-arrow': {
      border: 'solid #555555',
      borderWidth: '0 2px 2px 0'
    },
    '& .rmdp-header-values': {
      color: '#555555',
      fontSize: 20,
      fontWeight: 600
    },
    '& .rmdp-week-day': {
      color: '#9B9B9B'
    },
    '& .rmdp-shadow': {
      boxShadow: 'none'
    },
    '& .rmdp-calendar, .rmdp-header': {
      width: 450
    },
    '& .rmdp-week': {
      width: 400
    },
    '& .rmdp-day.rmdp-selected span:not(.highlight)': {
      boxShadow: 'none',
      backgroundColor: 'unset'
    },
    '& .rmdp-day:not(.rmdp-disabled,.rmdp-day-hidden) span:hover': {
      color: 'unset'
    },
    '& .rmdp-day.rmdp-today span': {
      color: '#386093'
    },
    '& .rmdp-day.rmdp-today': {
      border: 'solid #386093',
      borderRadius: 56
    },
    '&::-webkit-scrollbar': {
      width: 5,
      height: 5
    },
    '&::-webkit-scrollbar-track': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px',
      background: 'rgba(173, 184, 204, 1)'
    }
  },
  title: {
    fontWeight: 600,
    fontSize: 28,
    color: '#386093',
    margin: 0
  },
  calendar_wrapper: {
    padding: 16,
    width: 530,
    height: 400,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  event_card_componentes_wrapper: {
    padding: 24
  },
  // wrapper para dar o efeito do scroll interno
  event_card_wrapper: {
    width: 500,
    minHeight: 400,
    height: 'fit-content',
    maxHeight: 400,
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    borderRadius: 24
  },
  event_card: {
    maxHeight: 400,
    overflowY: 'auto',
    '& .title': {
      margin: 0,
      fontWeight: 600,
      fontSize: 20
    },
    '&::-webkit-scrollbar': {
      width: '5px'
    },
    '&::-webkit-scrollbar-track': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px',
      background: 'rgba(173, 184, 204, 1)'
    }
  },
  horizontal_bar: {
    height: 1,
    width: '100%',
    borderBottom: 'solid #ADB8CC26'
  },
  avatar: {
    '& span.userName': {
      fontSize: 16,
      fontWeight: 600
    }
  },
  event_overview: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8
  },
  event_overview_item: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '& .event': {
      backgroundColor: '#D9E8FACC',
      color: '#555555'
    },
    '& .start': {
      backgroundColor: '#EF7C8F',
      color: '#FFFFFF'
    },
    '& .end': {
      backgroundColor: '#F4A87C',
      color: '#FFFFFF'
    },
    '& .confirmation': {
      backgroundColor: '#E9FAD9',
      color: '#555555'
    },
    '& span.label': {
      fontSize: 12,
      fontWeight: 500
    }
  },
  event_overview_item_value: {
    padding: 8,
    borderRadius: 32,
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .value': {
      fontSize: 12,
      fontWeight: 500
    }
  },
  event_description: {
    marginTop: 24
  },
  events_cards: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16
  }
}))

const EventCalendar = ({ getEvents, events, history, isAdmin }) => {
  const classes = useStyles()
  const [viewDate, setViewDate] = React.useState(() => new DateObject())

  React.useEffect(() => {
    fetchByMonth(viewDate)
  }, [])

  const highlightedDays = React.useMemo(
    () => {
      const items = events?.items || []
      return new Set(
        items.map(item => {
          const d = new Date(item?.event_date)
          d.setHours(0, 0, 0, 0)
          return d.getTime()
        })
      )
    },
    [events?.items]
  )

  const fetchByMonth = dateObj => {
    const { beginningOfMonth, endOfMonth } = extractMonth(dateObj)
    getEvents({
      params: {
        'q[event_date_gteq]': beginningOfMonth,
        'q[event_date_lteq]': endOfMonth,
        'q[event_type_eq]': 'events'
      },
      isAdmin
    })
  }

  const handleViewChange = next => {
    setViewDate(next)
    fetchByMonth(next)
  }

  const handleClickItem = eventId => {
    history.push(`/announcements?id=${eventId}&name=view`)
  }

  return (
    <div className={classes.root}>
      <div className={classes.calendar_wrapper}>
        <h1 className={classes.title}>Calendário de eventos</h1>
        <CalendarComponent
          viewDate={viewDate}
          hideYear
          onDateChange={handleViewChange}
          mapDays={({ date }) => {
            const setedTimeStamp = new Date(
              date.year,
              date.month.index,
              date.day
            ).setHours(0, 0, 0, 0)
            const isHighlighted = highlightedDays.has(setedTimeStamp)
            return {
              style: {
                backgroundColor: isHighlighted ? '#EF7C8F' : 'unset',
                color: isHighlighted ? '#FFFFFF' : 'unset',
                cursor: 'pointer'
              }
            }
          }}
        />
      </div>
      <div className={classes.events_cards}>
        {events?.items.length > 0 &&
          events?.items.map((item, idx) => {
            return (
              <div key={idx} className={classes.event_card_wrapper}>
                <div
                  onClick={() => handleClickItem(item.id)}
                  className={classes.event_card}
                >
                  <div className={classes.event_card_componentes_wrapper}>
                    <h1 className='title'>{item.title}</h1>
                  </div>
                  <div className={classes.horizontal_bar} />
                  <div className={classes.event_card_componentes_wrapper}>
                    <UserAvatar
                      className={classes.avatar}
                      avatarStyle={{ width: 40, height: 40 }}
                      avatarUrl={item?.user?.avatar_url}
                      userName={item?.user?.name}
                    />
                    <div className={classes.event_overview}>
                      <div className={classes.event_overview_item}>
                        <span className='label'>Evento:</span>
                        <div
                          className={`${
                            classes.event_overview_item_value
                          } event`}
                        >
                          <span className='value'>
                            {moment(item.event_date).format('DD/MM/YYYY')}
                          </span>
                        </div>
                      </div>
                      <div className={classes.event_overview_item}>
                        <span className='label'>Início:</span>
                        <div
                          className={`${
                            classes.event_overview_item_value
                          } start`}
                        >
                          <span className='value'>
                            {moment(item.start_time).format('HH:mm')}
                          </span>
                        </div>
                      </div>
                      <div className={classes.event_overview_item}>
                        <span className='label'>Término:</span>
                        <div
                          className={`${classes.event_overview_item_value} end`}
                        >
                          <span className='value'>
                            {moment(item.end_time).format('HH:mm')}
                          </span>
                        </div>
                      </div>
                      <div className={classes.event_overview_item}>
                        <span className='label'>Confirmações:</span>
                        <div
                          className={`${
                            classes.event_overview_item_value
                          } confirmation`}
                        >
                          <span className='value'>
                            {item.participants.length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={classes.event_description}>
                      <p
                        className='text_content'
                        style={{ wordBreak: 'break-all' }}
                        dangerouslySetInnerHTML={{
                          __html: item.description
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default EventCalendar
